import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Wrapper from "../../components/wrapper/Wrapper";
import Header from "../../components/text/Heading";
import {
  createSocialMediaDetailAPI,
  deleteSocialMediaDetailAPI,
  editSocialMediaDetailAPI,
  getSocialMediaAPI,
} from "../../API/SocialMediaAPI/SocialMedia";
import ButtonLoader from "../../components/button/ButtonLoader";
import Button from "../../components/button/Button";
import PrimaryButton from "../../components/button/PrimaryButton";
import DeleteButton from "../../components/button/DeleteButton";
import CancelButton from "../../components/button/CancelButton";
import ModelWrapper from "../../components/Modal/ModelWrapper";
import InputField from "../../components/Form/InputField";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import CustomFileDropper from "../../components/dropers/CustomFileDropper";

const SocialMedia = () => {
  const queryClient = useQueryClient();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedSocialMedia, setSelectedSocialMedia] = useState({
    loader: false,
    id: null,
  });
  const [isSocialMediaLoading, setIsSocialMediaLoading] = useState(false);
  const [addSocialMedia, setAddSocialMedia] = useState(false);

  const { isLoading, data: socialMediaData } = useQuery({
    queryKey: ["SocialMedia"],
    queryFn: getSocialMediaAPI,
  });

  const toggleAddSocialMedia = () => {
    setAddSocialMedia((prev) => !prev);
    setIsEdit(false);
    setSelectedSocialMedia({ loader: false, id: null });
    formik.resetForm();
  };

  const handleEditClick = (item) => {
    setIsEdit(true);
    setSelectedSocialMedia({ loader: false, id: item._id });
    formik.setValues({
      socialMediaName: item.name,
      socialMediaLogo: item.imageUrl,
    });
    setAddSocialMedia(true);
  };

  const formik = useFormik({
    initialValues: {
      socialMediaName: "",
      socialMediaLogo: "",
    },
    validationSchema: Yup.object({
      socialMediaName: Yup.string().required("Social media name is required"),
      socialMediaLogo: Yup.string().required("Please upload social media logo"),
    }),
    onSubmit: (values) => {
      setIsSocialMediaLoading(true);
      const apiCall = isEdit
        ? editSocialMediaDetailAPI({
            id: selectedSocialMedia.id,
            name: values.socialMediaName,
            imageUrl: values.socialMediaLogo,
          })
        : createSocialMediaDetailAPI({
            name: values.socialMediaName,
            imageUrl: values.socialMediaLogo,
          });

      apiCall
        .then(() => {
          setIsSocialMediaLoading(false);
          setAddSocialMedia(false);
          toast.success("Success");
          queryClient.invalidateQueries("SocialMedia");
        })
        .catch((err) => {
          console.error("Error:", err);
          setIsSocialMediaLoading(false);
        });
    },
  });

  const handleSocialMediaDelete = (id) => {
    setSelectedSocialMedia({ loader: true, id });
    deleteSocialMediaDetailAPI(id)
      .then(() => {
        queryClient.invalidateQueries("SocialMedia");
        setSelectedSocialMedia({ loader: false, id: null });
        toast.success("Deleted successfully");
      })
      .catch((err) => {
        console.error("Error deleting social media:", err);
        setSelectedSocialMedia({ loader: false, id: null });
        toast.error("Error occur");
      });
  };

  if (isLoading) {
    return (
      <div className="py-10 w-full flex justify-center items-center">
        <ButtonLoader />
      </div>
    );
  }

  return (
    <Wrapper>
      <div className="flex justify-between items-end mb-4">
        <Header heading="Social Media Details" />
        <Button text="Add" onClick={toggleAddSocialMedia} />
      </div>

      <div>
        {socialMediaData?.data?.data?.map((item, index) => (
          <div
            key={index}
            className="flex flex-col sm:flex-row gap-2 text-[9px] sm:text-[10px] items-center bg-white rounded-lg mb-2 px-2 py-3 justify-between max-w-[320px] mx-auto"
          >
            <div className="flex gap-4 items-center">
              <div className="w-10 rounded-full">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="uppercase tracking-wide text-black">
                {item.name}
              </span>
            </div>
            <div className="flex gap-3">
              <PrimaryButton
                onClick={() => handleEditClick(item)}
                text="Edit"
                className="text-xs sm:text-xl"
                customClass="text-[9px] sm:text-[10px]"
              />
              <DeleteButton
                text={
                  selectedSocialMedia.id === item._id &&
                  selectedSocialMedia.loader
                    ? "Loading..."
                    : "Delete"
                }
                onClick={() => handleSocialMediaDelete(item._id)}
                customClass="text-[9px] sm:text-[10px]"
              />
            </div>
          </div>
        ))}
      </div>

      <ModelWrapper
        Title={isEdit ? "Edit Social Media" : "Add Social Media"}
        isOpen={addSocialMedia}
        setIsOpen={setAddSocialMedia}
      >
        <form onSubmit={formik.handleSubmit}>
          <InputField
            name="socialMediaName"
            id="socialMediaName"
            label="Social Media Name"
            placeholder="Name of social media"
            mainClass="mb-1"
            value={formik.values.socialMediaName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.socialMediaName}
            touch={formik.touched.socialMediaName}
          />
          <CustomFileDropper
            title="socialMediaLogo"
            instructions="upload logo here"
            onFileDrop={async (filePath, type) => {
              if (type === "image") {
                formik.setFieldValue("socialMediaLogo", filePath);
              }
            }}
            type="image"
            fileTypes={["image/jpeg", "image/png"]}
            defaultFile={
              formik.values.socialMediaLogo
                ? formik.values.socialMediaLogo
                : null
            }
          />
          <div className="flex gap-3 mt-4 justify-center items-center">
            {isSocialMediaLoading ? (
              <ButtonLoader />
            ) : (
              <>
                <PrimaryButton
                  type="submit"
                  text={isEdit ? "Update" : "Save"}
                />
                <CancelButton onClick={toggleAddSocialMedia} text="Cancel" />
              </>
            )}
          </div>
        </form>
      </ModelWrapper>
    </Wrapper>
  );
};

export default SocialMedia;
